import React from 'react';
import { Box, Stack, Typography, Tooltip, StackProps, Button } from '@mui/material';
import { ArrowUpward, ArrowDownward } from '@mui/icons-material';
import LoaderComponent from 'components/Loader';
import HighlightedText from 'components/GlobalSearch/HighlightText';
import CustomDropdown from 'components/Dropdown';
import { ReactComponent as RedirectIcon } from '../../../assets/PepWise2.0/Redirect-blue.svg';
import { useTheme } from '@mui/material/styles';
import { InfoTooltip, TooltipLabel } from '../InfoTooltip';

type KpiCardProps = {
  isLoading?: boolean;
  title: string | React.ReactNode;
  number: number | string;
  isCost?: boolean;
  isPercentage?: boolean;
  suffixFontSize?: number | string;
  change?: number;
  changeType?: 'increase' | 'decrease';
  comparePeriod?: "year" | "LAST_30_DAYS" | "LAST_60_DAYS" | "LAST_90_DAYS" | "LAST_120_DAYS";
  showChangePercentage?: boolean;
  showPeriodChange?: boolean;
  reverseTrendColor?: boolean;
  color?: string;
  numberColor?: string;
  units?: null | string | string[];
  handleEvent?: () => void;
  hasDropdown?: boolean;
  dropdownValue?: string;
  dropdownOptions?: { label: string; value: string }[];
  onDropdownChange?: (value: string) => void;
  containerProps?: StackProps;
  tooltipLabel?: TooltipLabel;
  hasTooltip?: boolean;
  isTextTooltip?: boolean;
  hoverText?: string;
  isSelected?: boolean;
  hasModal?: boolean;
  modalText?: string;
  onModalClick?: () => void;
  theme?: 'dark' | 'light';
  isCustomLabel?: boolean;
  customLabelText?: string;
  customLabelProps?: any;
  numbersx?: any;
};

const KpiCardNew = ({
  isLoading = false,
  title,
  number,
  change,
  changeType = 'increase',
  comparePeriod = 'year',
  showChangePercentage = false,
  showPeriodChange = true,
  color,
  numberColor,
  units = null,
  isCost = false,
  isPercentage = false,
  suffixFontSize = 20,
  reverseTrendColor = true,
  handleEvent,
  hasDropdown = false,
  dropdownValue,
  dropdownOptions = [],
  onDropdownChange,
  containerProps,
  tooltipLabel,
  hasTooltip = false,
  isTextTooltip = false,
  hoverText,
  isSelected = false,
  hasModal = false,
  modalText = 'View',
  onModalClick,
  theme = 'dark',
  isCustomLabel = false,
  customLabelText,
  customLabelProps,
  numbersx
}: KpiCardProps) => {

  const muiTheme = useTheme();
  const colors = muiTheme.palette;

  const resolvedTitleColor  = color       ?? colors.widget.text.main;
  const resolvedNumberColor = numberColor ?? colors.widget.text.main;

  const isClickable = !!handleEvent || !!containerProps?.onClick;
  const cursorStyle = isClickable ? 'pointer' : 'default';

  const getChangeColor = () => {
    if (change === undefined || change === 0) return colors.kpiTile?.text?.main;
    if (reverseTrendColor) return changeType === 'increase' ? colors.kpiTile?.text?.changeUp : colors.kpiTile?.text?.changeDown;
    return changeType === 'increase' ? colors.kpiTile?.text?.changeDown : colors.kpiTile?.text?.changeUp;
  };

  const periodLabelMap: Record<string, string> = {
    year: 'last year',
    LAST_30_DAYS: 'last month',
    LAST_60_DAYS: 'last 60 days',
    LAST_90_DAYS: 'last 90 days',
    LAST_120_DAYS: 'last 120 days',
  };

  const formatNumber = (value: number | string): string | number => {
    if (typeof value !== 'number') return value;
    if (Number.isInteger(value)) return value;
    return value.toFixed(isPercentage ? 1 : 2);
  };

  const getPercentageText = () => {
    if (change === 0) return '0%';
    const periodText = showPeriodChange ? 'vs ' + periodLabelMap[comparePeriod] : '';
    return `${change}% ${periodText}`;
  };

  const displayNumberWithUnits = ({
    value,
    unit,
    color: numColor,
    isCost: cost,
  }: {
    value: number | string;
    unit?: string;
    color?: string;
    isCost?: boolean;
  }) => (
    <Stack direction="row" alignItems="baseline" spacing={1}>
      <Typography
        variant="widgetValueNew"
        sx={{
          fontSize: { xs: 32, sm: 40, md: 60 },
          fontWeight: 500,
          color: numColor,
          display: 'flex',
          alignItems: 'baseline',
          gap: 1,
          ...numbersx
        }}
        
      >
        {cost && <Box component="span">$</Box>}
        <Typography variant="widgetValueNew" component="span" sx={{ fontWeight: 400 }}>{formatNumber(value)}</Typography>
        {isPercentage && <Box component="span">%</Box>}
        {unit && (
          <Box
            component="span"
            sx={{ fontSize: suffixFontSize, fontWeight: 500, ml: 1 }}
          >
            {unit}
          </Box>
        )}
      </Typography>
    </Stack>
  );



  // ── Number content (with optional hover tooltip) ───────────────────────────
  const renderNumberContent = () => {
    if (isLoading) return <LoaderComponent />;

    const content = displayNumberWithUnits({
      value: number,
      unit: Array.isArray(units) ? units[0] : units ?? undefined,
      color: resolvedNumberColor,
      isCost,
    });

    if (hoverText) {
      return (
        <Tooltip
          title={
            <Box sx={{ color: colors.widget.text.main, fontSize: '12px' }}>
              {hoverText}
            </Box>
          }
          arrow
          followCursor
          placement="right"
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: `${colors.widget.background.card} !important`,
                border: `1px solid ${colors.kpiTile?.text?.main}`,
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                '& .MuiTooltip-arrow': { color: colors.widget.background.card },
              },
            },
          }}
        >
          <Box sx={{ cursor: cursorStyle }}>{content}</Box>
        </Tooltip>
      );
    }

    return content;
  };

  return (
    <Box
      display="flex"
      flexGrow={1}
      height="100%"
      onClick={handleEvent}
      sx={{ cursor: cursorStyle }}
      {...containerProps}
    >
      <Stack
        minHeight="140px"
        p={2}
        borderRadius="5px"
        flexGrow={1}
        sx={{
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          cursor: cursorStyle,
          transition: 'all 0.25s ease-in-out',
          bgcolor: colors.widget.background.card,
          // ── identical border-bottom behaviour across both themes ──
          borderBottom: isSelected ? `4px solid ${colors.kpiTile?.background?.border}` : '4px solid transparent',
          ...(isClickable && {
            '&:hover': {
              boxShadow: 4,
              transform: 'translateY(-2px)',
              backgroundColor: colors.widget.background.card,
            },
          }),
          ...containerProps?.sx,
        }}
      >
        {/* Title Row */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
          <Typography
            variant="widgetTitleNew"
            sx={{
              color: resolvedTitleColor,
              fontSize: 20,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {typeof title === 'string' ? <HighlightedText text={title} /> : title}
            {isTextTooltip && hasTooltip && !!tooltipLabel ? <InfoTooltip label={tooltipLabel} /> : null}
          </Typography>

          <Stack direction="row" alignItems="center" gap={1}>
            {!isTextTooltip && hasTooltip && !!tooltipLabel ? <InfoTooltip label={tooltipLabel} /> : null}

            {hasDropdown && dropdownValue && dropdownOptions.length > 0 && (
              <CustomDropdown
                value={dropdownValue}
                options={dropdownOptions}
                handleChange={(e: any) => onDropdownChange?.(e.target.value)}
                boxProps={{
                  sx: { '& .MuiSelect-select': { color: colors.widget.text.main } },
                }}
              />
            )}
          </Stack>
        </Stack>

        {/* Number */}
        <Stack flexGrow={1} justifyContent="center" width="100%">
          {renderNumberContent()}
        </Stack>

        {/* Change Percentage row — same for both themes */}
        {showChangePercentage && change !== undefined && (
          <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" mt={1}>
            <Box display="flex" alignItems="center" color={getChangeColor()}>
              {change !== 0 && (
                changeType === 'increase'
                  ? <ArrowUpward fontSize="small" />
                  : <ArrowDownward fontSize="small" />
              )}
              <Typography ml={0.5} sx={{ fontWeight: 500, color: getChangeColor() }}>
                {getPercentageText()}
              </Typography>
            </Box>

            {hasModal && (
              <Button
                variant="text"
                size="small"
                onClick={(e) => { e.stopPropagation(); onModalClick?.(); }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  color: colors.widget.text.action.primary,
                  '&:hover': { backgroundColor: colors.widget.background.card },
                }}
              >
                {modalText}
                <RedirectIcon style={{ color: colors.widget.text.action.primary, width: 16, height: 16, marginLeft: 4 }} />
              </Button>
            )}
          </Box>
        )}
        {isCustomLabel && (
          <Typography ml={0.5} sx={{ color: colors.kpiTile?.text?.main, fontWeight: '500'}} {...customLabelProps}>
                {customLabelText}
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

export default KpiCardNew;